import React from 'react';
import { FormattedText } from '@/components/uiKit/FormattedText';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/providers/AuthProvider';
import { FieldValues, useForm } from 'react-hook-form';
import { locations } from '@/locations';
import { AuthError } from 'firebase/auth';
import { TOAST_STATUS } from '@/const';
import { mapAuthCodeToMessage } from '@/utils';
import { useToast } from '@/hooks/shared/use-toast';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { cn } from '@/lib/utils';

type ResetPasswordFormData = {
  email: string;
};

export const ResetPasswordForm = () => {
  const { toast } = useToast();
  const navigate = useNavigate();
  const { resetPassword } = useAuth();
  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting, isValid },
  } = useForm<ResetPasswordFormData>();

  const onSubmit = async (values: FieldValues) => {
    const { email } = values;

    if (isValid) {
      try {
        await resetPassword(email.trim());
        navigate(locations.public.sent);
      } catch (error: unknown) {
        const authError = error as AuthError;

        toast({
          variant: 'destructive',
          title: mapAuthCodeToMessage(authError.code),
        });
      }
    }
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <div className="space-y-4">
        <div className="space-y-2">
          <Label htmlFor="email">
            <FormattedText label="auth.login.form.email.label" />
          </Label>
          <Input
            id="email"
            type="text"
            placeholder=" "
            className={cn(errors.email && "border-red-500")}
            {...register('email', {
              required: 'Email is required',
            })}
          />
          {errors.email && (
            <p className="text-sm text-red-500 font-medium">{errors.email.message}</p>
          )}
        </div>
      </div>

      <div className="flex flex-col xl:flex-row mt-8 gap-4">
        <Button
          type="button"
          variant="ghost"
          onClick={() => navigate(locations.public.login)}
          className="text-blue-600 hover:text-blue-700 w-full xl:w-auto"
        >
          <FormattedText label="auth.resetPassword.email.form.backLabel" />
        </Button>

        <Button
          type="submit"
          className="w-full xl:flex-1"
          disabled={isSubmitting}
        >
            {isSubmitting ? (
              <span className="flex items-center">
                <svg className="animate-spin -ml-1 mr-3 h-5 w-5" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                Loading...
              </span>
            ) : (
              <FormattedText label="auth.resetPassword.email.form.submit" />
            )}
        </Button>
      </div>
    </form>
  );
};